"use client";

import React from "react";
import { Building2, MapPin, Globe, AlertCircle, Calendar, Hash, Check, X, Pencil, Plus } from "lucide-react";
import { CompanyInfoCard, InfoRow } from "./CompanyInfoCard";
import { ClampedText } from "@/components/shared/ClampedText";
import { CollapsibleSection } from "@/components/shared/CollapsibleSection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useCompanyHooks } from "@/lib/hooks/api/shared/companies-graphql";
import { toast } from "sonner";
import type { CompanyEntity } from "@/lib/types/company";

interface CompanyExecutiveSummaryProps {
  company: CompanyEntity;
  onRefresh?: () => void;
}

/**
 * Inline-editable company description. When a description is on file it renders
 * as clamped text with a hover pencil; when it's missing the fallback message
 * is paired with an "Add description" button that flips the same textarea open
 * in place — so the user can fix the missing data without leaving this card
 * (an empty/blocked state must always offer the remediation, never just
 * describe the gap). Persists via useCompanyHooks().useUpdate().
 */
function EditableCompanyDescription({
  company,
  description,
  onRefresh,
}: {
  company: CompanyEntity;
  description: string | null;
  onRefresh?: () => void;
}) {
  const { useUpdate } = useCompanyHooks();
  const { mutateAsync, isPending } = useUpdate();

  // Local mirror so the saved value shows immediately even without a refetch.
  const [value, setValue] = React.useState<string | null>(description);
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(description ?? "");

  // Re-seed when the underlying company changes (e.g. navigating between companies).
  React.useEffect(() => {
    setValue(description);
    if (!isEditing) setDraft(description ?? "");
  }, [description, company.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = () => {
    setDraft(value ?? "");
    setIsEditing(true);
  };

  const cancel = () => {
    setDraft(value ?? "");
    setIsEditing(false);
  };

  const save = async () => {
    const next = draft.trim();
    if (next === (value ?? "")) {
      setIsEditing(false);
      return;
    }
    try {
      await mutateAsync({ id: company.id, description: next || null });
      setValue(next || null);
      setIsEditing(false);
      toast.success("Company description updated");
      onRefresh?.();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update description"
      );
    }
  };

  if (isEditing) {
    return (
      <div className="mb-4">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Description
        </span>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Describe what this company does so AI summaries and your team have account context…"
          rows={3}
          autoFocus
          disabled={isPending}
          className="mt-1 text-sm"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void save();
            }
          }}
        />
        <div className="mt-2 flex items-center gap-2">
          <Button type="button" size="sm" onClick={() => void save()} disabled={isPending}>
            <Check className="h-4 w-4 mr-1" />
            {isPending ? "Saving…" : "Save"}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={cancel} disabled={isPending}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <span className="text-xs text-gray-400">⌘/Ctrl+Enter to save · Esc to cancel</span>
        </div>
      </div>
    );
  }

  if (value) {
    return (
      <div className="mb-4 group/desc">
        <InfoRow
          label={
            <span className="inline-flex items-center gap-1">
              Description
              <button
                type="button"
                onClick={startEdit}
                className="opacity-0 group-hover/desc:opacity-100 focus:opacity-100 transition-opacity text-gray-400 hover:text-gray-600"
                title="Edit description"
                aria-label="Edit company description"
              >
                <Pencil className="h-3 w-3" />
              </button>
            </span>
          }
          value={
            <ClampedText
              text={value}
              lines={3}
              className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
            />
          }
        />
      </div>
    );
  }

  // Missing description — describe the gap AND let the user fix it right here.
  return (
    <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-md">
      <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <p className="text-sm text-amber-800 dark:text-amber-200">
          No company description on file. Add one so AI summaries and your team have account context beyond notes and activity.
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={startEdit}
          className="mt-2 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add description
        </Button>
      </div>
    </div>
  );
}

export function CompanyExecutiveSummary({ company, onRefresh }: CompanyExecutiveSummaryProps) {
  const location = [company.city, company.state, company.country]
    .filter(Boolean)
    .join(", ");

  const description =
    company.description && typeof company.description === "string"
      ? company.description.trim()
      : null;

  const hasStatus = company.status;

  // Format dates
  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return null;
    try {
      return new Date(date).toLocaleDateString("en-US", { 
        year: "numeric", 
        month: "short", 
        day: "numeric" 
      });
    } catch {
      return null;
    }
  };

  const createdDate = formatDate(company.created_at);
  const updatedDate = formatDate(company.updated_at);

  return (
    <CompanyInfoCard title="Executive Summary" icon={Building2} className="col-span-2">
      {hasStatus && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</span>
          <Badge variant={company.status === "active" ? "default" : "secondary"}>
            {company.status}
          </Badge>
        </div>
      )}

      <EditableCompanyDescription
        company={company}
        description={description}
        onRefresh={onRefresh}
      />

      <div className="grid grid-cols-2 gap-4">
        {company.industry && (
          <InfoRow label="Industry" value={company.industry} icon={Building2} />
        )}
        
        {location && (
          <InfoRow label="Location" value={location} icon={MapPin} />
        )}

        {company.website && (
          <InfoRow 
            label="Website" 
            value={
              <a 
                href={company.website.startsWith("http") ? company.website : `https://${company.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline break-all"
              >
                {company.website}
              </a>
            }
            icon={Globe}
          />
        )}

        {company.domain && (
          <InfoRow label="Domain" value={company.domain} icon={Globe} />
        )}
      </div>

      {/* Company Metadata — low-signal audit/system fields, collapsed by default */}
      <CollapsibleSection title="System Details" defaultCollapsed className="mt-4">
        <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
          {company.id && (
            <div className="flex items-start gap-1.5">
              <Hash className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-medium">ID:</span>
                <p className="font-mono text-[10px] break-all">{company.id}</p>
              </div>
            </div>
          )}
          {createdDate && (
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3 w-3 flex-shrink-0" />
              <span>Created {createdDate}</span>
            </div>
          )}
          {updatedDate && (
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3 w-3 flex-shrink-0" />
              <span>Updated {updatedDate}</span>
            </div>
          )}
          {company.normalized_name && (
            <div className="flex items-start gap-1.5">
              <Building2 className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-medium">Normalized:</span>
                <p className="text-gray-600 dark:text-gray-400">{company.normalized_name}</p>
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>
    </CompanyInfoCard>
  );
}
